import {
  useMemo, useState,
} from 'react';
import {
  ButtonComponent, ButtonSize,
} from '~/_shared/baseComponents/buttons';
import {
  type DropdownOption,
  RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { useTranslation } from '~/_shared/utils/hooks';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import {
  buttonStyle,
  dropdownTriggerStyle,
  labelStyle, settingStyle,
} from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.styles';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { getDemographicsRegionsOptions } from '~/store/demographics/demographics.helpers';
import type { DemographicRegion } from '~/store/demographics/demographics.types';
import type { DataLayerTemplateResponse } from '~/store/frontendState/mapTools/dataLayers/dataLayers.repository';

enum AddLayerAction {
  OpenSavedLayerGroup,
  CreateNewLayerGroup,
}

export const DataLayersToolChoicesComponent = ({
  boundaryGroups,
  savedLayerGroups,
  onDone,
}: {
  boundaryGroups: ReadonlyArray<BoundaryGroup>;
  savedLayerGroups: ReadonlyArray<DataLayerTemplateResponse>;
  onDone?: () => void;
}) => {
  const [t] = useTranslation();
  const { openModal: openDataLayerCreate } = useModal(ModalType.DataLayerCreate);

  const [action, setAction] = useState<AddLayerAction | null>(null);
  const [selectedLayerGroupId, setSelectedLayerGroupId] = useState<number | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<DemographicRegion | null>(null);
  const [selectedBoundaryGroupId, setSelectedBoundaryGroupId] = useState<BoundaryGroupId | null>(null);

  const actionOptions: DropdownOption<AddLayerAction>[] = useMemo(() => [
    { name: t('dataLayersTool.openSavedLayerGroup'), value: AddLayerAction.OpenSavedLayerGroup },
    { name: t('dataLayersTool.createNewLayerGroup'), value: AddLayerAction.CreateNewLayerGroup },
  ], [t]);

  const selectedLayerGroup = savedLayerGroups.find(group => group.id === selectedLayerGroupId);
  const regionOptions = getDemographicsRegionsOptions({ t, onlyPremiumDataRegions: true });

  const activeRegion = action === AddLayerAction.CreateNewLayerGroup
    ? selectedRegion
    : selectedLayerGroup?.data.region || null;

  const boundaryGroupOptions: DropdownOption<BoundaryGroupId>[] = useMemo(() =>
    boundaryGroups
      .filter(group => group.region === activeRegion)
      .map(group => ({
        name: group.name,
        value: group.id,
      })),
  [boundaryGroups, activeRegion]);

  const savedLayerGroupOptions: DropdownOption<number>[] = useMemo(() =>
    savedLayerGroups.map(template => ({
      name: template.data.title,
      value: template.id,
    })),
  [savedLayerGroups]);

  const handleActionChange = (newAction: AddLayerAction | null) => {
    setAction(newAction);
    setSelectedLayerGroupId(null);
    setSelectedRegion(null);
    setSelectedBoundaryGroupId(null);
  };

  const handleRegionChange = (region: DemographicRegion | null) => {
    setSelectedRegion(region);
    setSelectedBoundaryGroupId(null);
  };

  const handleDataLayeeCreateOpen = () => {
    if (!selectedBoundaryGroupId) {
      return;
    }

    openDataLayerCreate({
      boundaryGroupId: selectedBoundaryGroupId,
      initialRegion: activeRegion || undefined,
    });

    onDone?.();
  };

  const isFormValid = (() => {
    switch (action) {
      case AddLayerAction.OpenSavedLayerGroup:
        return selectedLayerGroupId !== null;
      case AddLayerAction.CreateNewLayerGroup:
        return selectedRegion !== null && selectedBoundaryGroupId !== null;
      default:
        return false;
    }
  })();

  return (
    <>
      <div css={settingStyle}>
        <span css={labelStyle}>
          1. {t('dataLayersTool.selectDataLayerAction')}
        </span>
        <RegularDropdownComponent<AddLayerAction>
          inPortal
          onChange={handleActionChange}
          options={actionOptions}
          triggerStyle={dropdownTriggerStyle({ isPlaceholder: action === null })}
          value={action}
          placeholder={t('dataLayersTool.chooseOption')}
        />
      </div>

      {action === AddLayerAction.OpenSavedLayerGroup && (
        <div css={settingStyle}>
          <span css={labelStyle}>
            2. {t('dataLayersTool.selectDataLayerGroup')}
          </span>
          <RegularDropdownComponent<number>
            inPortal
            onChange={setSelectedLayerGroupId}
            options={savedLayerGroupOptions}
            triggerStyle={dropdownTriggerStyle({ isPlaceholder: selectedLayerGroupId === null })}
            value={selectedLayerGroupId}
            placeholder={t('dataLayersTool.chooseOption')}
          />
        </div>
      )}

      {action === AddLayerAction.CreateNewLayerGroup && (
        <div css={settingStyle}>
          <span css={labelStyle}>
            2. {t('Select Region')}
          </span>
          <RegularDropdownComponent<DemographicRegion>
            inPortal
            onChange={handleRegionChange}
            options={regionOptions}
            triggerStyle={dropdownTriggerStyle({ isPlaceholder: selectedRegion === null })}
            value={selectedRegion}
            placeholder={t('dataLayersTool.chooseOption')}
          />
        </div>
      )}

      {(
        (action === AddLayerAction.CreateNewLayerGroup && selectedRegion !== null)
          || (action === AddLayerAction.OpenSavedLayerGroup && selectedLayerGroup)
      ) && (
        <div css={settingStyle}>
          <span css={labelStyle}>
            3. {t('Select Boundary Type')}
          </span>
          <RegularDropdownComponent<number>
            inPortal
            onChange={setSelectedBoundaryGroupId}
            options={boundaryGroupOptions}
            triggerStyle={dropdownTriggerStyle({ isPlaceholder: selectedBoundaryGroupId === null })}
            value={selectedBoundaryGroupId}
            placeholder={t('dataLayersTool.chooseOption')}
          />
        </div>
      )}

      {action !== null && (
        <ButtonComponent
          css={buttonStyle}
          onClick={isFormValid ? handleDataLayeeCreateOpen : undefined}
          size={ButtonSize.Large}
          text={t('Continue')}
          isDisabled={!isFormValid}
        />
      )}
    </>
  );
};
